import { useQuery } from '@tanstack/react-query';
import { appConfigStore } from 'Models/AppConfigStore';
import { bookingType } from 'Models/Enums';
import { store } from 'Models/Store';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { exportBookingsWithFilters, exportSelectedBookings } from 'Services/Api/_HumanWritten/ReportService';
import { fetchAllRoutes } from 'Services/Api/_HumanWritten/RouteService';
import { stringIsEmpty, stringNotEmpty } from 'Util/TypeGuards';
import { BookingStatusTag, BookingStatusTagOption } from 'Views/Components/_HumanWritten/Bookings/BookingStatusTag';
import { Tag } from 'Views/Components/_HumanWritten/CheckIn/FerryCheckIn/CheckInList/Tag';
import { CustomTooltip } from 'Views/Components/_HumanWritten/CustomTooltip/CustomTooltip';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import Popover from 'Views/Components/_HumanWritten/Popover';
import {
	ClearFiltersButton,
	countActiveFilters,
} from 'Views/Components/_HumanWritten/StaffTables/Shared/ClearFiltersButton';
import { StaffBookingsColumnSelector } from 'Views/Components/_HumanWritten/StaffTables/StaffBookings/StaffBookingsColumnSelector';
import { StaffFiltersDatePicker } from 'Views/Components/_HumanWritten/StaffTables/StaffFiltersDatePicker';
import FhToggle from 'Views/Components/_HumanWritten/Toggle/FhToggle';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { Checkbox } from 'Views/Components/Checkbox/Checkbox';
import If from 'Views/Components/If/If';
import {
	defaultFilters as defaultBookingFilters,
	getBookingTypeDisplayName,
	getBookingTypesInApp,
	TravelStatusOption,
	useStaffBookingsStore,
} from './StaffBookingsFilterStore';

export interface StaffBookingsFiltersProps {
	setRefresh: () => void;
	totalBookingCount: number;
	userId?: string;
}

const StaffBookingFilters = ({ setRefresh, totalBookingCount, userId }: StaffBookingsFiltersProps) => {
	const intl = useIntl();

	// Get everything from Zustand store - single source of truth
	const {
		filters,
		activeTab,
		selectedStatuses,
		bookingTypes,
		travelStatusFilters,
		selectedRoutes,
		selectedBookingIds,
		visibleColumns,
		setSelectedBookingIds,
		setActiveTab,
		setSelectedStatuses,
		setSearchString,
		setBookingTypes,
		setTravelStatusFilters,
		setSelectedRoutes,
		updateFilters,
		resetFilters,
	} = useStaffBookingsStore();

	// Route options for the Route filter. Sourced from fetch-all-routes rather than the ferry-trips
	// `elements` endpoint, because that one is staff-only and this table is also served to invoiced
	// and fixed-price users. It is cache-backed server-side, hence the long staleTime.
	// Disabled routes are deliberately NOT filtered out — bookings made before a route was disabled
	// still exist and must stay findable.
	const { data: routes } = useQuery({
		queryKey: ['allRoutes'],
		queryFn: fetchAllRoutes,
		staleTime: 1000 * 60 * 5,
	});

	const routeOptions = (routes ?? [])
		.map(r => ({ name: r.name, value: r.id }))
		.sort((a, b) => a.name.localeCompare(b.name));

	// Local state for temporary selections (before popover closes)
	const [tempSelectedStatuses, setTempSelectedStatuses] = useState<BookingStatusTagOption[]>([]);
	const [tempSelectedBookingTypes, setTempSelectedBookingTypes] = useState<bookingType[]>([]);
	const [tempTravelStatusFilters, setTempTravelStatusFilters] = useState<TravelStatusOption[]>([]);
	const [tempSelectedRoutes, setTempSelectedRoutes] = useState<string[]>([]);
	const [tempSearchString, setTempSearchString] = useState(filters.searchString || '');

	const [exporting, setExporting] = useState(false);

	const statusOptions: { name: string; displayName: string; value: BookingStatusTagOption }[] = [
		{
			name: 'Cancelled',
			displayName: intl.formatMessage({
				id: 'booking-status__cancelled__label',
				defaultMessage: 'Cancelled',
			}),
			value: BookingStatusTagOption.CANCELLED,
		},
		{
			name: 'Paid',
			displayName: intl.formatMessage({
				id: 'booking-status__paid__label',
				defaultMessage: 'Paid',
			}),
			value: BookingStatusTagOption.PAID,
		},
		{
			name: 'Awaiting',
			displayName: intl.formatMessage({
				id: 'booking-status__awaiting__label',
				defaultMessage: 'Awaiting',
			}),
			value: BookingStatusTagOption.AWAITING,
		},
		{
			name: 'Invoiced',
			displayName: intl.formatMessage({
				id: 'booking-status__invoiced__label',
				defaultMessage: 'Invoiced',
			}),
			value: BookingStatusTagOption.INVOICED,
		},
	];

	// Offer the Unpaid filter when either creating unpaid bookings is allowed, or the dedicated
	// "show unpaid status" setting is on (so existing unpaid bookings stay findable after new
	// pay-later bookings are disabled).
	if (appConfigStore.ShowUnpaidStatusTag) {
		statusOptions.push({
			name: 'Unpaid',
			displayName: intl.formatMessage({
				id: 'booking-status__unpaid__label',
				defaultMessage: 'Unpaid',
			}),
			value: BookingStatusTagOption.UNPAID,
		});
	}

	const bookingTypeOptions = () => {
		return getBookingTypesInApp().map((b: any) => ({
			name: b,
			displayName: getBookingTypeDisplayName(b),
		}));
	};

	// Helper function to check if two arrays are equal
	const arraysEqual = (
		arr1: BookingStatusTagOption[] | bookingType[] | TravelStatusOption[],
		arr2: BookingStatusTagOption[] | bookingType[] | TravelStatusOption[],
	) => {
		if (arr1.length !== arr2.length) return false;
		const sorted1 = [...arr1].sort();
		const sorted2 = [...arr2].sort();
		return sorted1.every((value, index) => value === sorted2[index]);
	};

	useEffect(() => {
		setTempSearchString(filters.searchString || '');
	}, [filters.searchString]);

	// Debounce search. The `|| ''` matters: without it an empty box compared unequal to an absent
	// store value on every mount, which wrote to the store and forced a refetch of a table that was
	// already showing the right rows.
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			if (tempSearchString !== (filters.searchString || '')) {
				setSearchString(tempSearchString);
				setRefresh();
			}
		}, 500);

		return () => clearTimeout(timeoutId);
	}, [tempSearchString]);

	// Sync temp state when store state changes
	useEffect(() => {
		setTempSelectedStatuses(selectedStatuses);
	}, [selectedStatuses]);

	useEffect(() => {
		setTempSelectedBookingTypes(bookingTypes);
	}, [bookingTypes]);

	useEffect(() => {
		setTempTravelStatusFilters(travelStatusFilters);
	}, [travelStatusFilters]);

	useEffect(() => {
		setTempSelectedRoutes(selectedRoutes);
	}, [selectedRoutes]);

	const getStatusDisplay = () => {
		if (selectedStatuses.length === 0 || selectedStatuses.length === statusOptions.length) {
			return 'All';
		}
		const tags: JSX.Element[] = [];
		selectedStatuses.forEach((status, index) => {
			tags.push(<BookingStatusTag status={status} isStaff={store.isStaff} />);
		});
		return tags;
	};

	const getBookingTypeDisplay = () => {
		if (bookingTypes.length === 0 || bookingTypes.length === bookingTypeOptions().length) {
			return 'All';
		}

		const tags = bookingTypeOptions()
			.filter(opt => bookingTypes.includes(opt.name))
			.map((opt, index) => <Tag text={opt.displayName} tagClassName="grey" />);

		return <>{tags}</>;
	};

	const getTravelStatusDisplay = () => {
		const travelStatusOptions: TravelStatusOption[] = ['Active', 'Travelled', 'Cancelled'];

		if (travelStatusFilters.length === 0 || travelStatusFilters.length === travelStatusOptions.length) {
			return 'All';
		}

		const tags = travelStatusFilters.map((status, index) => <Tag key={status} text={status} tagClassName="grey" />);

		return <>{tags}</>;
	};

	// Kept apart from `onAfterClose` deliberately. That handler reverts an emptied selection to the
	// previous one; the route filter instead treats both empty and all-selected as "all routes",
	// matching the Ferry Trips filter this one mirrors.
	const routeArraysEqual = (arr1: string[], arr2: string[]) => {
		if (arr1.length !== arr2.length) return false;
		const sorted1 = [...arr1].sort();
		const sorted2 = [...arr2].sort();
		return sorted1.every((value, index) => value === sorted2[index]);
	};

	const getRouteDisplay = () => {
		if (selectedRoutes.length === 0 || selectedRoutes.length === routeOptions.length) {
			return 'All';
		}

		const tags = selectedRoutes
			.map(id => routeOptions.find(opt => opt.value === id))
			.filter(opt => opt !== undefined)
			.map(opt => <Tag key={opt!.value} text={opt!.name} tagClassName="grey" />);

		return <>{tags}</>;
	};

	const onAfterCloseRoutes = () => {
		// All selected collapses to [] so the clause never enters the query.
		const effective = tempSelectedRoutes.length === routeOptions.length ? [] : tempSelectedRoutes;
		if (!routeArraysEqual(effective, selectedRoutes)) {
			setSelectedRoutes(effective);
			requestAnimationFrame(() => {
				setRefresh();
			});
		}
	};

	const onAfterClose = () => {
		const statusesChanged = !arraysEqual(tempSelectedStatuses, selectedStatuses);
		const bookingTypesChanged = !arraysEqual(tempSelectedBookingTypes, bookingTypes);
		const travelStatusChanged = !arraysEqual(tempTravelStatusFilters, travelStatusFilters);

		// Reset temp state if empty
		if (tempSelectedStatuses.length === 0) {
			setTempSelectedStatuses(selectedStatuses);
			return;
		}

		if (tempSelectedBookingTypes.length === 0) {
			setTempSelectedBookingTypes(bookingTypes);
			return;
		}

		if (tempTravelStatusFilters.length === 0) {
			setTempTravelStatusFilters(travelStatusFilters);
			return;
		}

		// Apply changes if anything changed
		if (statusesChanged || bookingTypesChanged || travelStatusChanged) {
			// Batch updates together
			if (statusesChanged) {
				setSelectedStatuses(tempSelectedStatuses);
			}
			if (bookingTypesChanged) {
				setBookingTypes(tempSelectedBookingTypes);
			}
			if (travelStatusChanged) {
				setTravelStatusFilters(tempTravelStatusFilters);
			}

			// Trigger refresh after state has settled
			requestAnimationFrame(() => {
				setRefresh();
			});
		}
	};

	const handleToggleChange = (newTab: string) => {
		setActiveTab(newTab);
		setRefresh();
	};

	const handleDatePickerChange = (startDate: Date | undefined, endDate: Date | undefined) => {
		updateFilters({
			startDate,
			endDate,
			pageNo: 0,
			bookingFilterType: 'All',
		});
		setActiveTab('All');
		setRefresh();
	};

	const handleFilterTypeChange = (filterByCreatedDate: boolean) => {
		updateFilters({
			filterByCreatedDate,
			pageNo: 0,
		});
		setRefresh();
	};

	const isAllStatusesSelected = tempSelectedStatuses.length === statusOptions.length;
	const isAllBookingTypesSelected = tempSelectedBookingTypes.length === bookingTypeOptions().length;
	const isAllRoutesSelected = tempSelectedRoutes.length === routeOptions.length;
	const travelStatusOptions: TravelStatusOption[] = ['Active', 'Travelled', 'Cancelled'];
	const isAllTravelStatusSelected = tempTravelStatusFilters.length === travelStatusOptions.length;

	// Calculate export count based on current state
	const count = selectedBookingIds.size > 0 ? selectedBookingIds.size : totalBookingCount;
	return (
		<div className="table-filters__container">
			{/* Active/Past Toggle */}
			<div className="left">
				<FhToggle
					options={['All', 'Active', 'Past/Cancelled']}
					active={activeTab}
					onChange={handleToggleChange}
				/>

				{/* Trip Date Range / Created date */}
				<StaffFiltersDatePicker
					options={['Trip Date', 'Created Date']}
					startDate={filters.startDate}
					endDate={filters.endDate}
					filterByCreatedDate={filters.filterByCreatedDate}
					onDateChange={handleDatePickerChange}
					onFilterTypeChange={handleFilterTypeChange}
				/>

				{/* Status Multi-select with Popover */}
				<Popover
					popoverClassName="fh-popover"
					onAfterClose={onAfterClose}
					buttonContent={(() => {
						return (
							<>
								<p className="grey">Status</p>
								<span className="tags">{getStatusDisplay()}</span>
							</>
						);
					})()}
					popoverContent={() => (
						<div>
							<Checkbox
								className="fh-popover__checkbox fh-popover__list-item"
								model={{
									value: isAllStatusesSelected,
								}}
								modelProperty="value"
								label="All"
								onChecked={(_, value) => {
									if (!value) {
										setTempSelectedStatuses([]);
									} else {
										setTempSelectedStatuses(statusOptions.map(option => option.value));
									}
								}}
							/>
							{statusOptions.map(statusOption => {
								return (
									<Checkbox
										className="fh-popover__checkbox fh-popover__list-item"
										key={statusOption.name}
										model={{ value: tempSelectedStatuses.includes(statusOption.value) }}
										modelProperty="value"
										label={statusOption.displayName}
										onChecked={(_, value) => {
											if (value) {
												const newStatuses = [...tempSelectedStatuses, statusOption.value];
												setTempSelectedStatuses(newStatuses);
											} else {
												setTempSelectedStatuses(
													tempSelectedStatuses.filter(x => x !== statusOption.value),
												);
											}
										}}
									/>
								);
							})}
						</div>
					)}
				/>
				{/* Booking type with Popover */}
				<Popover
					popoverClassName="fh-popover"
					onAfterClose={onAfterClose}
					buttonTestId="staff-bookings-filter-booking-type"
					buttonContent={(() => {
						return (
							<>
								<p className="grey">Booking Type</p>
								<span className="tags">{getBookingTypeDisplay()}</span>
							</>
						);
					})()}
					popoverContent={() => (
						<div>
							<Checkbox
								className="fh-popover__checkbox fh-popover__list-item"
								model={{
									value: isAllBookingTypesSelected,
								}}
								modelProperty="value"
								label="All"
								onChecked={(_, value) => {
									if (!value) {
										setTempSelectedBookingTypes([]);
									} else {
										setTempSelectedBookingTypes(bookingTypeOptions().map(option => option.name));
									}
								}}
							/>
							{bookingTypeOptions().map(option => {
								return (
									<Checkbox
										className="fh-popover__checkbox fh-popover__list-item"
										key={option.name}
										model={{ value: tempSelectedBookingTypes.includes(option.name) }}
										modelProperty="value"
										label={option.displayName}
										onChecked={(_, value) => {
											if (value) {
												const newBookingTypes = [...tempSelectedBookingTypes, option.name];
												setTempSelectedBookingTypes(newBookingTypes);
											} else {
												setTempSelectedBookingTypes(
													tempSelectedBookingTypes.filter(x => x !== option.name),
												);
											}
										}}
									/>
								);
							})}
						</div>
					)}
				/>
				{/* Route with Popover */}
				<Popover
					popoverClassName="fh-popover"
					onAfterClose={onAfterCloseRoutes}
					buttonTestId="staff-bookings-filter-route"
					buttonContent={
						<>
							<p className="grey">Route</p>
							<span className="tags">{getRouteDisplay()}</span>
						</>
					}
					popoverContent={() => (
						<div>
							<Checkbox
								className="fh-popover__checkbox fh-popover__list-item"
								model={{
									value: isAllRoutesSelected,
								}}
								modelProperty="value"
								label="All"
								onChecked={(_, value) => {
									if (!value) {
										setTempSelectedRoutes([]);
									} else {
										setTempSelectedRoutes(routeOptions.map(option => option.value));
									}
								}}
							/>
							{routeOptions.map(option => {
								return (
									<Checkbox
										className="fh-popover__checkbox fh-popover__list-item"
										key={option.value}
										model={{ value: tempSelectedRoutes.includes(option.value) }}
										modelProperty="value"
										label={option.name}
										onChecked={(_, value) => {
											if (value) {
												setTempSelectedRoutes([...tempSelectedRoutes, option.value]);
											} else {
												setTempSelectedRoutes(
													tempSelectedRoutes.filter(x => x !== option.value),
												);
											}
										}}
									/>
								);
							})}
						</div>
					)}
				/>
				{/* Travel Status with Popover */}
				<Popover
					popoverClassName="fh-popover"
					onAfterClose={onAfterClose}
					buttonContent={(() => {
						return (
							<>
								<p className="grey">Travel Status</p>
								<span className="tags">{getTravelStatusDisplay()}</span>
							</>
						);
					})()}
					popoverContent={() => (
						<div>
							<Checkbox
								className="fh-popover__checkbox fh-popover__list-item"
								model={{
									value: isAllTravelStatusSelected,
								}}
								modelProperty="value"
								label="All"
								onChecked={(_, value) => {
									if (!value) {
										setTempTravelStatusFilters([]);
									} else {
										setTempTravelStatusFilters(travelStatusOptions);
									}
								}}
							/>
							{travelStatusOptions.map(option => {
								return (
									<Checkbox
										className="fh-popover__checkbox fh-popover__list-item"
										key={option}
										model={{ value: tempTravelStatusFilters.includes(option) }}
										modelProperty="value"
										label={option}
										onChecked={(_, value) => {
											if (value) {
												const newTravelStatus = [...tempTravelStatusFilters, option];
												setTempTravelStatusFilters(newTravelStatus);
											} else {
												setTempTravelStatusFilters(
													tempTravelStatusFilters.filter(x => x !== option),
												);
											}
										}}
									/>
								);
							})}
						</div>
					)}
				/>
				{/* Search input */}
				<form onSubmit={e => e.preventDefault()}>
					<div className="search-box">
						<input
							id="booking-search-input"
							placeholder="Search"
							className="fh-basic-search"
							autoComplete="off"
							value={tempSearchString}
							onChange={e => setTempSearchString(e.target.value)}
						/>
						<If condition={stringIsEmpty(tempSearchString)}>
							<CustomTooltip
								id="search-tool-tip"
								content={
									appConfigStore.systemWasMigrated
										? 'Search by Booking ID or Legacy ID'
										: 'Search by Booking ID only'
								}
								toolTipPosition="bottom-right"
							/>
						</If>
						<If condition={stringNotEmpty(tempSearchString)}>
							<Button
								className="clear-search"
								onClick={e => {
									e.preventDefault();
									setTempSearchString('');
								}}
								colors={Colors.Black}
								display={Display.Text}
							>
								<Icon name="cross" />
							</Button>
						</If>
					</div>
				</form>
				<ClearFiltersButton
					activeCount={countActiveFilters(filters, defaultBookingFilters)}
					onClear={() => {
						resetFilters(stringNotEmpty(userId));
						setTempSearchString('');
						setRefresh();
					}}
					dataTestId="staff-bookings-clear-filters"
				/>
			</div>
			<div className="right">
				<StaffBookingsColumnSelector />
				<If condition={!store.isManager && selectedBookingIds.size > 0}>
					<Button
						newStyles
						display={Display.Outline}
						colors={Colors.Secondary}
						onClick={() => {
							setSelectedBookingIds(new Set());
						}}
					>
						Clear selection
					</Button>
				</If>
				<div>
					<Button
						display={Display.Solid}
						colors={Colors.Secondary}
						className="export-button"
						newStyles
						disabled={exporting || (selectedBookingIds.size === 0 && totalBookingCount === 0)}
						onClick={async () => {
							setExporting(true);
							try {
								if (selectedBookingIds.size === 0) {
									await exportBookingsWithFilters({
										bookingFilterType: filters.bookingFilterType,
										bookingTypes: filters.bookingTypes,
										travelStatusFilters: filters.travelStatusFilters,
										routeFilters: filters.routeFilters,
										startDate: filters.startDate,
										endDate: filters.endDate,
										filterByCreatedDate: filters.filterByCreatedDate,
										statusFilters: filters.statusFilters,
										orderBy: filters.orderBy,
										orderByDescending: filters.orderByDescending,
										userId: userId,
										initialFetch: false,
										columns: visibleColumns.filter(x => x.visible).map(x => x.key),
									});
								} else {
									await exportSelectedBookings(
										Array.from(selectedBookingIds),
										visibleColumns.filter(x => x.visible).map(x => x.key),
									);
								}
							} finally {
								setExporting(false);
							}
						}}
					>
						<Icon name="download" useMaterialIcons />
						{exporting ? 'Exporting...' : 'Export'} ({count})
					</Button>
				</div>
			</div>
		</div>
	);
};

export default StaffBookingFilters;
